import { NotesCount } from "@/components/RecentReportsCard/components";
import { useAppConfigContext } from "@/context/AppConfig";
import { useReportContext } from "@/context/ReportContext";
import { useAccountNotes } from "@/dashboard/AccountNotes/useAccountNotes";
import { useReportDelete, useReportRedirect } from "@/reports/index";
import { PdfUpgradeWrapper } from "@/reports/shared/PdfAddon/PdfUpgradeWrapper";
import {
  Button,
  Icons,
  ListItemIcon,
  ListItemText,
  MenuItem as MUIMenuItem,
  Menu,
} from "@/theme/index";
import type { SxProps } from "@/theme/index";
import type { ValidReportTypes } from "@/utils/helperTypes";
import { scrollToElement } from "@/utils/scrollToElement";
import { useState } from "react";
import { ReportActionButton } from "../ReportActionButton/ReportActionButton";
import { useMoreAction } from "./useMoreAction";

export type MenuItem = {
  icon?: JSX.Element;
  display: string;
  action: () => void;
  disabled?: boolean;
  count?: JSX.Element | null;
  hidden?: boolean;
};

export interface MoreActionProps {
  filterMenuItems?: (items: MenuItem[]) => MenuItem[];
  disableIconStyles?: boolean;
  sx?: SxProps;
  reportPermaLink?: string;
  deleteCurrentReport: (permalink: string) => void;
  reportUpgraded?: boolean;
  isPersonReport?: boolean;
  onReportCard?: boolean;
  disableCollapse?: boolean;
  reportType?: ValidReportTypes | string;
}

export const MoreAction: React.FC<MoreActionProps> = ({
  filterMenuItems,
  sx,
  reportPermaLink,
  deleteCurrentReport,
  reportUpgraded,
  isPersonReport,
  onReportCard,
  disableCollapse,
  reportType,
}) => {
  const { trackEvent } = useAppConfigContext();
  const { redirectToReportUrl } = useReportRedirect();
  const { permalink: contextPermalink } = useReportContext();
  const permalink = reportPermaLink || contextPermalink;
  const { reportNotesCount } = useAccountNotes(permalink);
  const { generatePdf, generatingPdf, pdfModalState, setPdfModalState } =
    useMoreAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const reportDeleteMutation = useReportDelete(permalink, deleteCurrentReport);

  const notesTotalDisplay = () => {
    const notesTotal = reportNotesCount();
    if (notesTotal === null) return null;
    return <NotesCount>{notesTotal}</NotesCount>;
  };

  const defaultMenuItems: MenuItem[] = [
    {
      display: "PDF",
      action: generatePdf,
      icon: <Icons.PictureAsPdf fontSize="small" color="primary" />,
      disabled: generatingPdf,
    },
    {
      display: "Notes",
      action: () => {
        trackEvent("notes", "engagement click", "rats component");
        if (onReportCard) {
          redirectToReportUrl(
            reportType as ValidReportTypes,
            `${reportPermaLink}#Notes-section`,
          );
        } else {
          scrollToElement("Notes-section");
        }
      },
      icon: <Icons.Chat fontSize="small" color="primary" />,
      count: notesTotalDisplay(),
    },
    {
      display: "Delete",
      action: () => {
        trackEvent("delete", "engagement click", "rats component");
        reportDeleteMutation.mutate();
      },
      icon: <Icons.Clear fontSize="small" color="primary" />,
    },
    {
      display: reportUpgraded ? "Unlocked" : "Free Bonus",
      action: () => scrollToElement("court-records-section"),
      icon: reportUpgraded ? (
        <Icons.LockOpen fontSize="small" color="primary" />
      ) : (
        <Icons.Star fontSize="small" color="primary" />
      ),
      hidden: !isPersonReport,
      disabled: reportUpgraded,
    },
  ].filter((item) => !item.hidden);

  const moreMenuItems = filterMenuItems
    ? filterMenuItems(defaultMenuItems)
    : defaultMenuItems;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    trackEvent(
      "overflow menu entry point",
      "engagement click",
      "rats component",
    );
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleCloseItem = (
    event: React.MouseEvent<HTMLButtonElement>,
    clickedItem: MenuItem,
  ) => {
    event.stopPropagation();
    clickedItem.action();
    handleCloseMenu(event);
  };

  const handleGeneratePdf = () => {
    generatePdf();
  };

  return (
    <>
      <ReportActionButton
        collapseText="More"
        iconName="MoreHoriz"
        onClick={handleClick}
        sx={sx}
        onReportCard={onReportCard}
        disableCollapse={disableCollapse}
      />
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
        {moreMenuItems.map((item) => (
          <MUIMenuItem key={item.display}>
            <Button
              onClick={(event) => {
                if (onReportCard) {
                  item.action();
                }
                handleCloseItem(event, item);
              }}
              disabled={item.disabled}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.display} />
              {item.count && item.count}
            </Button>
          </MUIMenuItem>
        ))}
      </Menu>
      <PdfUpgradeWrapper
        modalState={pdfModalState}
        setModalState={setPdfModalState}
        reportPermalink={permalink}
        generatePDF={handleGeneratePdf}
      />
    </>
  );
};
